import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { FindEmailResponseType } from 'src/models/Account/FindEmail';
import { SignInRequestType, SigninResponseType } from 'src/models/Account/Signin';
import { SignUpRequestType, SignupResponseType } from 'src/models/Account/Signup';
import { CommonApiErrorActionType } from 'src/models/BaseApi';
import { BaseStoreEntityType, baseStoreEntityValue } from 'src/store/types';
import { currentUserSlice } from 'src/store/user/slice';
import { SignOutResponseType } from 'src/models/Account/SignOut';

export enum ModesEnum {
  emailAwaited = 'emailAwaited',
  passwordAwaited = 'passwordAwaited',
  passwordResetCaptcha = 'passwordResetCaptch',
  createAccount = 'createAccount',
  createPassword = 'createPassword',
}

export type SignInOutInitialStateType = {
  mode: ModesEnum;

  modal: boolean;

  enteredEmail: string | null;

  accountResponse: FindEmailResponseType | null;

  signIn: BaseStoreEntityType<SigninResponseType>;

  signUp: BaseStoreEntityType<SignupResponseType>;

  signOut: BaseStoreEntityType<SignOutResponseType>;
};

const initialState: SignInOutInitialStateType = {
  mode: ModesEnum.emailAwaited,
  modal: false,
  enteredEmail: null,
  accountResponse: null,
  signIn: baseStoreEntityValue(),
  signUp: baseStoreEntityValue(),
  signOut: baseStoreEntityValue(),
};

export const signInOutSlice = createSlice({
  name: 'signInOut',
  initialState,
  reducers: {
    changeAccountResponse: (state, { payload }: PayloadAction<SignInOutInitialStateType['accountResponse']>) => {
      state.accountResponse = payload;

      state.enteredEmail = payload?.Email || null;
    },

    changeMode: (state, { payload }: PayloadAction<ModesEnum>) => {
      state.mode = payload;

      /** reset some data */
      state.signIn = initialState.signIn;
      state.signUp = initialState.signUp;
    },

    changeModal: (state, { payload }: PayloadAction<boolean>) => {
      state.modal = payload;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    signinStart: (state, data: PayloadAction<SignInRequestType>) => {
      state.signIn = baseStoreEntityValue(null, true);
    },
    signinOk: (state, { payload }: PayloadAction<SigninResponseType>) => {
      state.signIn = baseStoreEntityValue(payload, false);

      state.enteredEmail = null;

      if (state.modal) {
        state.modal = false;
      }
    },
    signinError: (state, { payload }: PayloadAction<SigninResponseType>) => {
      state.signIn = baseStoreEntityValue(null, false, payload);
    },
    signinClear: (state) => {
      state.signIn = baseStoreEntityValue(null, false);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    signOutStart: (state, action: PayloadAction<string | undefined>) => {
      state.signOut = baseStoreEntityValue(null, true);
    },
    signOutOk: (state, { payload }: PayloadAction<SignOutResponseType>) => {
      state.signOut = baseStoreEntityValue(payload, false);
    },
    signOutError: (state, { payload }: PayloadAction<string>) => {
      state.signOut = baseStoreEntityValue(null, false, payload);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    signUpStart: (state, data: PayloadAction<SignUpRequestType>) => {
      state.signUp = baseStoreEntityValue(null, true);
    },
    signUpOk: (state, { payload }: PayloadAction<SignupResponseType>) => {
      state.signUp = baseStoreEntityValue(payload, false);

      state.enteredEmail = null;

      if (state.modal) {
        state.modal = false;
      }
    },
    signUpError: (state, { payload }: CommonApiErrorActionType) => {
      state.signUp = baseStoreEntityValue(null, false, payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(currentUserSlice.actions.getCurrentUserOk.type, (state) => {
      if (state.mode !== ModesEnum.emailAwaited) {
        state.mode = ModesEnum.emailAwaited; // reset mode after signin/signup
      }
    });
  },
});
